import React from 'react';
import { Ticket, TicketStatus, TicketPriority, TicketComplexity } from '../../types/api';
import { AlertCircle, Circle, CheckCircle, Clock, Tag, User, Calendar } from 'lucide-react';
import clsx from 'clsx';

interface TicketCardProps {
  ticket: Ticket;
  onClick?: (ticket: Ticket) => void;
}

const statusConfig: Record<TicketStatus, { icon: typeof Circle; color: string; bg: string }> = {
  open: { icon: Circle, color: 'text-blue-600', bg: 'bg-blue-50' },
  'in-progress': { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50' },
  blocked: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
  review: { icon: Clock, color: 'text-purple-600', bg: 'bg-purple-50' },
  closed: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
};

const priorityConfig: Record<TicketPriority, { color: string; bg: string }> = {
  critical: { color: 'text-red-700', bg: 'bg-red-100' },
  high: { color: 'text-orange-700', bg: 'bg-orange-100' },
  medium: { color: 'text-yellow-700', bg: 'bg-yellow-100' },
  low: { color: 'text-gray-700', bg: 'bg-gray-100' },
};

const complexityConfig: Record<TicketComplexity, { color: string; bg: string }> = {
  Jr: { color: 'text-green-700', bg: 'bg-green-100' },
  Sr: { color: 'text-blue-700', bg: 'bg-blue-100' },
  Lvl3: { color: 'text-purple-700', bg: 'bg-purple-100' },
};

export function TicketCard({ ticket, onClick }: TicketCardProps) {
  const statusInfo = statusConfig[ticket.status];
  const StatusIcon = statusInfo.icon;
  const priorityInfo = ticket.priority ? priorityConfig[ticket.priority] : null;
  const complexityInfo = ticket.complexity ? complexityConfig[ticket.complexity] : null;

  const createdDate = new Date(ticket.createdAt).toLocaleDateString();
  const updatedDate = new Date(ticket.updatedAt).toLocaleDateString();
  const dueDate = ticket.dueDate ? new Date(ticket.dueDate).toLocaleDateString() : null;

  const handleClick = () => {
    onClick?.(ticket);
  };

  return (
    <div
      className={clsx(
        'bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow',
        onClick && 'cursor-pointer'
      )}
      onClick={handleClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3 flex-1">
          <StatusIcon className={clsx('h-5 w-5 mt-1', statusInfo.color)} />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{ticket.title}</h3>
            <p className="text-sm text-gray-600 mt-1">{ticket.repoName}</p>
          </div>
        </div>
        <div className="flex flex-col gap-2 ml-4">
          {priorityInfo && (
            <span
              className={clsx(
                'px-2 py-1 rounded text-xs font-medium capitalize whitespace-nowrap',
                priorityInfo.color,
                priorityInfo.bg
              )}
            >
              {ticket.priority}
            </span>
          )}
          {complexityInfo && (
            <span
              className={clsx(
                'px-2 py-1 rounded text-xs font-medium whitespace-nowrap',
                complexityInfo.color,
                complexityInfo.bg
              )}
            >
              {ticket.complexity}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      {ticket.body && (
        <p className="text-sm text-gray-700 mb-4 line-clamp-2">{ticket.body}</p>
      )}

      {/* Tags */}
      {ticket.tags && ticket.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {ticket.tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
            >
              <Tag className="h-3 w-3" />
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {ticket.assignee && (
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {ticket.assignee}
            </div>
          )}
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Updated {updatedDate}
          </div>
          {dueDate && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Due {dueDate}
            </div>
          )}
        </div>

        <span
          className={clsx(
            'px-2 py-1 rounded-full text-xs font-medium capitalize',
            statusInfo.color,
            statusInfo.bg
          )}
        >
          {ticket.status}
        </span>
      </div>
    </div>
  );
}
